import { TIER_NAMES } from './constants';
import {
  EVENT_CATEGORIES, SEVERITY_LEVELS, STATUS_CODES, RADIUS_CODES,
  RESPONSE_FLAGS, RESOURCE_TYPES, RESOURCE_STATUS, TEXT_ENCODING,
} from './reference';
import type { DecodeResult } from './types';

const W = 58; // inner width

function pad(s: string, n: number): string {
  if (s.length >= n) return s.slice(0, n);
  return s + ' '.repeat(n - s.length);
}

function row(content: string): string {
  return '│ ' + pad(content, W - 2) + ' │';
}

function divider(label: string): string {
  const text = `─ ${label} `;
  const fill = '─'.repeat(Math.max(0, W - text.length - 2));
  return '├' + text + fill + '─┤';
}

function flagsToList(mask: number): string {
  const out: string[] = [];
  for (let bit = 15; bit >= 0; bit--) {
    if ((mask & (1 << bit)) !== 0) {
      const name = RESPONSE_FLAGS[bit];
      if (name && name !== 'Reserved') out.push(name);
    }
  }
  return out.length ? out.join(' · ') : '(none)';
}

function fmtTime(d: Date): string {
  const pad2 = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 3) + '...' : s;
}

/**
 * Render a decoded EMRG payload as a human-readable ASCII box, with all
 * lookup codes resolved to names (category, severity, status, radius, flags).
 * Used by the dashboard, the API encode/decode response, the decoder PWA,
 * and the MCP server output formatting.
 *
 * @param result - The output of `decode()`
 * @returns Multi-line monospace string with box-drawing borders
 */
export function prettyDecode(result: DecodeResult): string {
  const { payload, tier } = result;
  const lines: string[] = [];

  const title = `─ EMRG TIER ${tier} · ${TIER_NAMES[tier]} `;
  lines.push('┌' + title + '─'.repeat(Math.max(0, W - title.length - 2)) + '─┐');

  const seq = payload.envelope.sequence.toString().padStart(4, '0');
  const dev = '0x' + payload.envelope.deviceId.toString(16).toUpperCase().padStart(4, '0');
  lines.push(row(`Schema   EMRG v${payload.header.version[0]}.${payload.header.version[1]}        Seq    ${seq}`));
  lines.push(row(`Time     ${fmtTime(payload.envelope.timestamp)}   Dev    ${dev}`));
  lines.push(row(`Priority ${payload.envelope.priority}                  TTL    ${payload.envelope.ttl}`));

  lines.push(divider('EVENT'));
  const catName = EVENT_CATEGORIES[payload.event.category] ?? 'Unknown';
  const sev = SEVERITY_LEVELS[payload.event.severity];
  const sevName = sev ? sev.name.toUpperCase() : 'UNKNOWN';
  const statusName = STATUS_CODES[payload.event.status] ?? 'Unknown';
  const rad = RADIUS_CODES[payload.event.radius];
  const radName = rad ? `${rad.name} (${rad.metersMax !== null ? '<' + rad.metersMax + 'm' : 'wide'})` : 'Unknown';
  lines.push(row(`Type     ${pad(catName + ' (' + payload.event.category + ')', 22)}Sev    ${sevName} (${payload.event.severity})`));
  lines.push(row(`Status   ${pad(statusName + ' (' + payload.event.status + ')', 22)}Rad    ${radName}`));
  lines.push(row(`Flags    ${truncate(flagsToList(payload.event.responseFlags), W - 12)}`));

  lines.push(divider('LOCATION'));
  if (payload.location.lat !== undefined && payload.location.lng !== undefined) {
    lines.push(row(`Lat      ${pad(payload.location.lat.toFixed(6), 22)}Lng    ${payload.location.lng.toFixed(6)}`));
  } else {
    lines.push(row('No GPS fix'));
  }

  if (payload.resources.length > 0) {
    lines.push(divider(`RESOURCES (${payload.resources.length})`));
    payload.resources.forEach((r, i) => {
      const tName = RESOURCE_TYPES[r.type] ?? 'Unknown';
      const sName = RESOURCE_STATUS[r.status] ?? 'Unknown';
      lines.push(row(`[${i + 1}]      ${pad(tName, 22)}${sName}`));
    });
  }

  lines.push(divider('MESSAGE'));
  const ref = '0x' + payload.message.ref.toString(16).toUpperCase().padStart(4, '0');
  const enc = TEXT_ENCODING[payload.message.encoding];
  const encName = enc ? enc.name : 'Unknown';
  lines.push(row(`Ref      ${pad(ref, 22)}Enc    ${encName}`));
  lines.push(row(`Text     ${truncate(payload.message.text, W - 12)}`));

  lines.push('└' + '─'.repeat(W - 2) + '┘');
  return lines.join('\n');
}
